import React from "react"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { useToast } from "~/ui/use-toast"
import { EmailVerificationStep } from "./components/EmailVerificationStep"

const USER_EMAIL_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation VerifyEmailDialogUserEmailVerify($input: UserEmailVerifyInput!) {
    userEmailVerify(input: $input) {
      userEmail {
        id
        email
        status
      }
    }
  }
`)

const USER_EMAIL_RESEND_MUTATION = gql(/* GraphQL */ `
  mutation VerifyEmailDialogUserEmailResend($input: UserEmailResendInput!) {
    userEmailResend(input: $input) {
      userEmail {
        id
        email
        status
      }
    }
  }
`)

interface VerifyEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userEmailId: string
  email: string
  onSuccess?: () => void
}

export const VerifyEmailDialog: React.FC<VerifyEmailDialogProps> = ({
  open,
  onOpenChange,
  userEmailId,
  email,
  onSuccess,
}) => {
  const [userEmailVerify, userEmailVerifyResult] = useSafeMutation(USER_EMAIL_VERIFY_MUTATION)
  const [userEmailResend, userEmailResendResult] = useSafeMutation(USER_EMAIL_RESEND_MUTATION)
  const { toast } = useToast()

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleVerify = async (token: string) => {
    const result = await userEmailVerify({
      variables: {
        input: {
          userEmailId,
          token,
        },
      },
    })

    if (result.errors) {
      return
    }

    toast({
      title: "Email Verified",
      description: "Your email has been successfully verified.",
      variant: "default",
    })
    onSuccess?.()
    handleClose()
  }

  const handleResend = async () => {
    const result = await userEmailResend({
      variables: {
        input: {
          userEmailId,
        },
      },
    })

    if (result.errors) {
      return
    }

    toast({
      title: "Verification Code Sent",
      description: "A new verification code has been sent to your email.",
      variant: "default",
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enter Verification Code</DialogTitle>
        </DialogHeader>
        <EmailVerificationStep
          email={email}
          onVerify={handleVerify}
          onSendAgain={handleResend}
          onCancel={handleClose}
          verificationError={userEmailVerifyResult.error}
          isVerifying={userEmailVerifyResult.loading}
          isSendingAgain={userEmailResendResult.loading}
          description={`A verification code was sent to ${email}. Please check your email and input the code provided.`}
        />
        {userEmailResendResult.error && (
          <div className="px-6 pb-4">
            <GraphqlError error={userEmailResendResult.error} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
